import {
  CompositeFilterDescriptor,
  SortDescriptor,
} from '@progress/kendo-data-query';
import {
  Grid,
  GridColumn as Column, GridColumnProps,
  GridColumnReorderEvent,
  GridFilterChangeEvent,
  GridNoRecords,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import { SingleSectionPageCard } from 'Components/Display';
import { GridProvider, useGridState, useOptionalGridState } from 'Context';
import { buildEndpoint, fullClassName, toggledClass, useApiWorker } from 'Utilities';
import { isEqual } from 'lodash';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import './Grid.scss';
import { NoRecordsAlert } from './NoRecordsAlert';
import { useAsyncGridState } from './hooks/useAsyncGridState';
import { AsyncGridProps } from './types';
import Cloudy from 'Assets/Icons/cloudy.svg';
import { GridSkeleton } from './GridSkeleton';
import { EventBusy } from '@mui/icons-material';
import { columnGap } from '@mui/system';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { name } from 'faker';

export const AsyncGridContent: React.FC<AsyncGridProps> = ({
  children,
  withCardWrapper,
  isMainGrid,
  className,
  hideNoRecordsAlert,
  leftAlignNoRecords,
  customNoRecordsDisplay,
  withBorder,
  onFiltersChange,
  onSortingChange,
  filtersEnabled,
  noRecordsTitle,
  noRecordsDescription,
  dataItemKey = 'id',
  forceEmpty = false,
  gridId,
  ...otherProps
}) => {
  const [pagination, setPagination] = React.useState({
    skip: 0,
    take: otherProps?.pageSize ?? 50,
  });

  const {
    hasData,
    isFetching,
    dataState,
    resetStateToFirstPage,
    setDataState,
    fetchSilently,
    ...gridProps
  } = useAsyncGridState({ dataItemKey, ...otherProps });

  const gridState = useGridState();
  const API = useApiWorker();
  const [currentFilterId, setCurrentFilterId] = useState<string>();
  // Convert children -> column props for state
  const initialCols = React.Children.toArray(children)
    .map((child: any) => child.props);

  const [columns, setColumns] = React.useState(initialCols);

  const Wrapper = withCardWrapper ? SingleSectionPageCard : Fragment;
  const wrapperProps = withCardWrapper ? { centerContent: true } : undefined;
  const shouldShowCustomNoRecordsDisplay =
    !hasData && customNoRecordsDisplay !== undefined;
  const gridRef = useRef<Grid | null>(null);

  // Sync columns with children when they change (e.g. dropdown options load)
  useEffect(() => {
    const nextCols = React.Children.toArray(children).map((child: any) => child.props);

    setColumns(prevCols => {
      const prevFields = new Set(prevCols.map(c => c.field));
      const nextFields = new Set(nextCols.map(c => c.field));

      
      const columnSetChanged =
        nextCols.some(c => !prevFields.has(c.field)) ||
        prevCols.some(c => !nextFields.has(c.field));

      if (columnSetChanged) {
        return nextCols;
      }

   
      const nextColsMap = new Map(nextCols.map(c => [c.field, c]));
      const updatedCols = prevCols.map(prevCol => {
        const nextCol = nextColsMap.get(prevCol.field);
        if (nextCol) {
          return { ...prevCol, ...nextCol };
        }
        return prevCol;
      });

      return isEqual(prevCols, updatedCols) ? prevCols : updatedCols;
    });
  }, [children]);

  useEffect(() => {
    if (gridRef.current !== null) {
      const columnIds = gridRef.current.columns
        .filter((col) => col.id !== undefined && col.width === 'auto')
        .map((col) => col.id) as string[];
      gridRef.current.fitColumns && gridRef.current.fitColumns(columnIds);
    }
  }, []);

  const GetUserGridPreferencesEndpoint = buildEndpoint(
    ENDPOINTS.userpreferences.endpointString('GetGridPreference')
  );
  const SaveUserGridPreferencesEndpoint = buildEndpoint(
    ENDPOINTS.userpreferences.endpointString('GridPreference')
  );

  useEffect(() => {
    if (!gridId) return;
    const fetchData = async () => {
      const { data, headers } = await API.post(
        GetUserGridPreferencesEndpoint,
        {
          preferenceType: 'GridColumnOrder',
          gridId: `${gridId}`,
        },
        {
          responseType: 'json',
        }
      );
      if (data && data.layoutJson) {
        const savedColumns: GridColumnProps[] = JSON.parse(data.layoutJson);
        const currentColumns = gridRef.current?.columns || [];
        const updatedColumns = currentColumns.map((col: any) => {
          const savedCol = savedColumns.find(
            (sc) => sc.field === col.field
          );
          return {
            ...col,
            orderIndex: savedCol ? savedCol.orderIndex : undefined,
          };
        });
        updatedColumns.sort((a: any, b: any) => {
          const orderA = a.orderIndex ?? 0;
          const orderB = b.orderIndex ?? 0;
          return orderA - orderB;
        });
        setColumns(updatedColumns);
      }
    };
    fetchData();
  }, [gridId]);


  const initialSort: SortDescriptor[] = [{ field: 'id', dir: 'asc' }];
  const [filter, setFilter] = React.useState<CompositeFilterDescriptor>({
    logic: 'and',
    filters: [],
  });
  const [sort, setSort] = React.useState<SortDescriptor[]>(initialSort);
  const gridWrapperRef = useRef<HTMLDivElement>(null);

  const filterChange = (event: GridFilterChangeEvent) => {
    if (isEqual(event.filter, filter)) return;

    const newFilters = event.filter.filters.map((f) => {
      if ('field' in f && f.value && f.value !== '') {
        return { [f.field as string]: f.value };
      }
      return {};
    });
    const filtersObj = Object.assign({}, ...newFilters);

    onFiltersChange && onFiltersChange(filtersObj);
    setFilter(event.filter);
  };

  const handleSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);

    if (event.sort.length > 0) {
      if (pagination.skip !== 0) {
        setPagination((p) => ({ ...p, skip: 0 }));
      }
      onSortingChange?.([
        { field: event.sort[0].field, dir: event.sort[0].dir },
      ]);
    }

    setDataState((prev) => ({
      ...prev,
      sort: event.sort,
      skip: 0,
    }));
  };

  const handlePageChange = (event: any) => {
    const newPage = { skip: event.page.skip, take: event.page.take };
    if (newPage.skip === pagination.skip && newPage.take === pagination.take)
      return;

    setPagination(newPage);
    setDataState((prev) => ({
      ...prev,
      skip: newPage.skip,
      take: newPage.take,
    }));
  };

  const onColumnReorder = (event: GridColumnReorderEvent) => {
    if (!gridId) return;
    const reordered = [...event.columns];

    const locked = reordered.filter(c => c.locked);
    const unlocked = reordered.filter(c => !c.locked);
    unlocked.sort((a, b) => {
      const orderA = a.orderIndex ?? 0;
      const orderB = b.orderIndex ?? 0;
      return orderA - orderB;
    });

    const merged = [...locked, ...unlocked];

    const normalized = merged.map((col, i) => ({
      ...col,
      orderIndex: i
    }));
    setColumns(normalized);

    API.post(SaveUserGridPreferencesEndpoint, {
      preferenceType: 'GridColumnOrder',
      gridId: `${gridId}`,
      layoutJson: JSON.stringify(normalized)
    });
  };

  return (
    <Wrapper {...wrapperProps}>
      <div className="table-container" ref={gridWrapperRef}>
        <div className="full-width">
          <div className="grid-loading-wrapper">
            {isFetching && (
              <div className="grid-loading-overlay">
                <GridSkeleton columns={columns.length} />
              </div>
            )}
            <Grid
              onColumnReorder={onColumnReorder}
              dataItemKey={dataItemKey}
              filterable={filtersEnabled}
              filter={filter}
              onFilterChange={filterChange}
              sortable
              sort={sort}
              onSortChange={handleSortChange}
              {...gridProps}
              total={gridProps.total || 1}
              {...otherProps}
              {...dataState}
              data={forceEmpty ? [] : gridProps.data}
              onPageChange={handlePageChange}
              skip={pagination?.skip}
              take={pagination?.take}
              pageable={{ pageSizes: [10, 25, 50, 100, 200], type: 'input' }}
              reorderable={true}
              resizable={false}
              pageSize={50}
              ref={(ref) => (gridRef.current = ref)}
              className={fullClassName(
                className,
                toggledClass('main-grid', isMainGrid),
                toggledClass('with-border', withBorder),
                toggledClass(
                  'with-custom-no-records',
                  shouldShowCustomNoRecordsDisplay
                )
              )}
            >
              <GridNoRecords>
                <div
                  style={{
                    background: '#F6F7F9',
                    padding: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <img
                    src={Cloudy}
                    alt="No records"
                    style={{ width: 100, marginBottom: 10 }}
                  />
                  <span className="no-records-title">
                    {noRecordsTitle || 'No records found'}
                  </span>
                  <span className="no-records-description">
                    {noRecordsDescription ||
                      'Try adjusting your filters or adding new items to see results here.'}
                  </span>
                </div>
              </GridNoRecords>
              {columns.map((col, i) => (
                <Column key={col.field || i} {...col} />
              ))}
            </Grid>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export const AsyncGrid: React.FC<AsyncGridProps> = ({ ...gridProps }) => {
  const gridState = useOptionalGridState();
  var Wrapper = GridProvider;
  if (gridState !== undefined) {
    Wrapper = Fragment;
  }

  return (
    <Wrapper>
      <AsyncGridContent {...gridProps} />
    </Wrapper>
  );
};

export const MainGrid: React.FC<AsyncGridProps> = ({
  isMainGrid,
  resizable,
  ...allProps
}) => {
  return <AsyncGrid isMainGrid {...allProps} />;
};
